import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/Button'
import { useNavigate } from 'react-router-dom'

export function DashboardPage() {
  const { user, isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()

  if (!isAuthenticated) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-8">You need to be signed in to access the dashboard.</p>
          <Button>Sign In</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.first_name || user?.username}!
        </h1>
        <p className="text-gray-600">
          Manage your self-hosting services and account settings.
        </p>
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Username</label>
            <p className="mt-1 text-sm text-gray-900">{user?.username}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">First Name</label>
            <p className="mt-1 text-sm text-gray-900">{user?.first_name || 'Not set'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Last Name</label>
            <p className="mt-1 text-sm text-gray-900">{user?.last_name || 'Not set'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Account Type</label>
            <div className="mt-1">
              {user?.is_admin ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Administrator
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  User
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Roles</label>
            <div className="mt-1 flex flex-wrap gap-1">
              {user?.roles.map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">My Services</h3>
          <p className="text-gray-600 text-sm mb-4">
            View and manage your deployed self-hosting services.
          </p>
          <Button variant="outline" className="w-full" onClick={() => navigate('/services')}>
            View Services
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Orders & Billing</h3>
          <p className="text-gray-600 text-sm mb-4">
            Check your order history and billing information.
          </p>
          <Button variant="outline" className="w-full" onClick={() => navigate('/orders')}>
            View Orders
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Browse Packages</h3>
          <p className="text-gray-600 text-sm mb-4">
            Explore available self-hosting service packages.
          </p>
          <Button className="w-full" onClick={() => navigate('/packages')}>
            Browse Packages
          </Button>
        </div>
      </div>

      {/* Admin Section */}
      {isAdmin() && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-purple-900 mb-4">
            🔧 Administrator Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button variant="outline" className="justify-start" onClick={() => navigate('/admin')}>
              🔧 Admin Panel
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate('/admin')}>
              📦 Manage Packages
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate('/admin')}>
              🛒 Manage Orders
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate('/admin')}>
              ⚙️ System Status
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}